import { assignmentsEndIn24hours, assignmentsEndIn7Days } from '../utils/assignmentUtils';
import type { StudentAssignment } from './student/hooks/useStudentAssignments';
import { TeacherAssignment } from './teacher/api/AssignmentTeacherApi';

export function splitAssignmentsByEndDateCategory<T extends TeacherAssignment | StudentAssignment>(
  assignments: T[],
): {
  assignmentsEndIn24hours: T[];
  assignmentsEndIn7Days: T[];
  assignmentsEndOver7Days: T[];
} {
  const assignmentsEndingIn24hours: T[] = [];
  const assignmentsEndingIn7Days: T[] = [];
  const assignmentsEndingOver7Days: T[] = [];

  for (const assignment of assignments) {
    if (assignmentsEndIn24hours(assignment)) {
      assignmentsEndingIn24hours.push(assignment);
    } else if (assignmentsEndIn7Days(assignment)) {
      assignmentsEndingIn7Days.push(assignment);
    } else {
      assignmentsEndingOver7Days.push(assignment);
    }
  }

  return {
    assignmentsEndIn24hours: assignmentsEndingIn24hours,
    assignmentsEndIn7Days: assignmentsEndingIn7Days,
    assignmentsEndOver7Days: assignmentsEndingOver7Days,
  };
}

function getMonthYear(date: Date, language: string) {
  const dateLanguage = language === 'nl' ? 'nl-BE' : 'fr-BE';

  return date.toLocaleString(dateLanguage, {
    year: 'numeric',
    month: 'long',
  });
}

export function splitAssignmentsByPastEndDateCategory<T extends TeacherAssignment | StudentAssignment>(
  assignments: T[],
  language: string,
) {
  const now = new Date();

  const day = now.getDay();
  const diffToMonday = (day === 0 ? -6 : 1) - day;
  const startOfThisWeek = new Date(now);
  startOfThisWeek.setDate(now.getDate() + diffToMonday);
  startOfThisWeek.setHours(0, 0, 0, 0);

  const startOfLastWeek = new Date(startOfThisWeek);
  startOfLastWeek.setDate(startOfThisWeek.getDate() - 7);

  const endOfLastWeek = new Date(startOfThisWeek);

  const endedThisWeek: T[] = [];
  const endedLastWeek: T[] = [];
  const endedByMonth: Record<string, T[]> = {};

  for (const assignment of assignments) {
    const score = (assignment as StudentAssignment).score;
    const dateString = score ? score.achievedAt ?? assignment.to : assignment.to;
    const dueDate = new Date(dateString);

    if (dueDate >= startOfThisWeek) {
      endedThisWeek.push(assignment);
    } else if (dueDate >= startOfLastWeek && dueDate <= endOfLastWeek) {
      endedLastWeek.push(assignment);
    } else if (dueDate < startOfLastWeek) {
      const key = getMonthYear(dueDate, language);
      if (!endedByMonth[key]) {
        endedByMonth[key] = [];
      }
      endedByMonth[key].push(assignment);
    }
  }

  return { endedThisWeek, endedLastWeek, endedByMonth };
}
